import { catalog, flattenTechniques, type KataId } from '../../data/catalog'
import {
  emptyNoteDocument,
  isNoteDocumentEmpty,
  plainTextToNoteDocument,
  validateNoteDocument,
} from '../notes/document'
import { getKataScoring, type MistakeSeverity } from '../../data/scoring'
import type { ElementMarkState } from '../../lib/scoring/engine'
import {
  SCHEMA_VERSION,
  personalRecordId,
  type AppPreferences,
  type Favourite,
  type KataPerformance,
  type TechniqueNote,
  type TechniqueSegment,
  type VideoReference,
} from '../schema'

/** Export payload — personal data only; no videos or cache assets. */
export interface ExportPayload {
  schemaVersion: number
  exportedAt: string
  references: VideoReference[]
  notes: TechniqueNote[]
  favourites: Favourite[]
  preferences: AppPreferences
  performances: KataPerformance[]
}

export type ImportPreview = {
  schemaVersion: number
  exportedAt?: string
  referenceCount: number
  segmentCount: number
  noteCount: number
  favouriteCount: number
  performanceCount: number
  hasPreferences: boolean
}

export type ValidateImportResult =
  | { ok: true; payload: ExportPayload; preview: ImportPreview }
  | { ok: false; error: string }

export const MAX_IMPORT_BYTES = 2 * 1024 * 1024
export const MAX_REFERENCES = 50
export const MAX_SEGMENTS_PER_REF = 150
export const MAX_NOTES = 300
export const MAX_FAVOURITES = 300
export const MAX_PERFORMANCES = 500
/** @deprecated Prefer MAX_NOTE_JSON_CHARS for BlockNote documents. */
export const MAX_NOTE_CHARS = 20_000
export const MAX_NOTE_JSON_CHARS = 100_000
export const MAX_NOTE_BLOCKS = 500
export const SUPPORTED_IMPORT_SCHEMA_VERSIONS = [1, 2, 3, 4] as const
const SEVERITIES = new Set<MistakeSeverity>(['small', 'medium', 'big'])

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/

function knownCatalog(): { kataIds: Set<string>; techniqueIds: Set<string>; techniquesByKata: Map<string, Set<string>> } {
  const kataIds = new Set<string>()
  const techniqueIds = new Set<string>()
  const techniquesByKata = new Map<string, Set<string>>()
  for (const kata of catalog) {
    kataIds.add(kata.id)
    const set = new Set<string>()
    for (const tech of flattenTechniques(kata)) {
      techniqueIds.add(tech.id)
      set.add(tech.id)
    }
    techniquesByKata.set(kata.id, set)
  }
  return { kataIds, techniqueIds, techniquesByKata }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isIsoTimestamp(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_RE.test(value)) return false
  const t = Date.parse(value)
  return Number.isFinite(t)
}

function isKataId(value: unknown, kataIds: Set<string>): value is KataId {
  return typeof value === 'string' && kataIds.has(value)
}

function validateSegment(
  raw: unknown,
  path: string,
  allowedTechniques: Set<string>,
): TechniqueSegment | string {
  if (!isPlainObject(raw)) return `${path} must be an object.`
  const { techniqueId, startSeconds, endSeconds } = raw
  if (typeof techniqueId !== 'string' || !allowedTechniques.has(techniqueId)) {
    return `${path}.techniqueId is unknown for this kata.`
  }
  if (typeof startSeconds !== 'number' || !Number.isFinite(startSeconds) || startSeconds < 0) {
    return `${path}.startSeconds must be a finite number ≥ 0.`
  }
  if (typeof endSeconds !== 'number' || !Number.isFinite(endSeconds) || endSeconds < 0) {
    return `${path}.endSeconds must be a finite number ≥ 0.`
  }
  if (!(startSeconds < endSeconds)) {
    return `${path}: startSeconds must be before endSeconds.`
  }
  return { techniqueId, startSeconds, endSeconds }
}

function validateReference(
  raw: unknown,
  index: number,
  catalogIndex: ReturnType<typeof knownCatalog>,
): VideoReference | string {
  if (!isPlainObject(raw)) return `references[${index}] must be an object.`
  const path = `references[${index}]`
  const { id, kataId, title, provider, videoId, segments, createdAt, updatedAt } = raw

  if (typeof id !== 'string' || !id.trim()) return `${path}.id is required.`
  if (!isKataId(kataId, catalogIndex.kataIds)) return `${path}.kataId is not a known kata.`
  if (typeof title !== 'string') return `${path}.title must be a string.`
  if (provider !== 'youtube') return `${path}.provider must be "youtube".`
  if (typeof videoId !== 'string' || !YOUTUBE_ID_RE.test(videoId)) {
    return `${path}.videoId must be a valid YouTube video ID.`
  }
  if (!isIsoTimestamp(createdAt)) return `${path}.createdAt must be an ISO timestamp.`
  if (!isIsoTimestamp(updatedAt)) return `${path}.updatedAt must be an ISO timestamp.`
  if (!Array.isArray(segments)) return `${path}.segments must be an array.`
  if (segments.length > MAX_SEGMENTS_PER_REF) {
    return `${path}.segments exceeds limit (${MAX_SEGMENTS_PER_REF}).`
  }

  const allowed = catalogIndex.techniquesByKata.get(kataId)!
  const parsedSegments: TechniqueSegment[] = []
  const seenTech = new Set<string>()
  for (let i = 0; i < segments.length; i++) {
    const seg = validateSegment(segments[i], `${path}.segments[${i}]`, allowed)
    if (typeof seg === 'string') return seg
    if (seenTech.has(seg.techniqueId)) {
      return `${path}.segments: duplicate techniqueId ${seg.techniqueId}.`
    }
    seenTech.add(seg.techniqueId)
    parsedSegments.push(seg)
  }

  return {
    id: id.trim(),
    kataId,
    title,
    provider: 'youtube',
    videoId,
    segments: parsedSegments,
    createdAt,
    updatedAt,
  }
}

function validateNote(
  raw: unknown,
  index: number,
  catalogIndex: ReturnType<typeof knownCatalog>,
): TechniqueNote | string {
  if (!isPlainObject(raw)) return `notes[${index}] must be an object.`
  const path = `notes[${index}]`
  const { id, kataId, techniqueId, body, content, createdAt, updatedAt } = raw
  if (!isKataId(kataId, catalogIndex.kataIds)) return `${path}.kataId is unknown.`
  if (typeof techniqueId !== 'string' || !catalogIndex.techniquesByKata.get(kataId)!.has(techniqueId)) {
    return `${path}.techniqueId is unknown for kata.`
  }
  if (!isIsoTimestamp(createdAt) || !isIsoTimestamp(updatedAt)) {
    return `${path} timestamps must be ISO strings.`
  }

  let document
  if (content !== undefined) {
    document = validateNoteDocument(content, `${path}.content`, {
      maxBlocks: MAX_NOTE_BLOCKS,
      maxJsonChars: MAX_NOTE_JSON_CHARS,
    })
    if (typeof document === 'string') return document
  } else if (typeof body === 'string') {
    if (body.length > MAX_NOTE_CHARS) {
      return `${path}.body exceeds ${MAX_NOTE_CHARS} characters.`
    }
    document = plainTextToNoteDocument(body)
  } else {
    return `${path} must include content (BlockNote blocks) or legacy body (plain text).`
  }

  if (isNoteDocumentEmpty(document)) {
    document = emptyNoteDocument()
  }

  const expectedId = personalRecordId(kataId, techniqueId)
  const noteId = typeof id === 'string' && id.trim() ? id.trim() : expectedId
  return {
    id: noteId,
    kataId,
    techniqueId,
    content: document,
    createdAt,
    updatedAt,
  }
}

function validateFavourite(
  raw: unknown,
  index: number,
  catalogIndex: ReturnType<typeof knownCatalog>,
): Favourite | string {
  if (!isPlainObject(raw)) return `favourites[${index}] must be an object.`
  const path = `favourites[${index}]`
  const { id, kataId, techniqueId, createdAt } = raw
  if (!isKataId(kataId, catalogIndex.kataIds)) return `${path}.kataId is unknown.`
  if (typeof techniqueId !== 'string' || !catalogIndex.techniquesByKata.get(kataId)!.has(techniqueId)) {
    return `${path}.techniqueId is unknown for kata.`
  }
  if (!isIsoTimestamp(createdAt)) return `${path}.createdAt must be an ISO timestamp.`
  const expectedId = personalRecordId(kataId, techniqueId)
  const favId = typeof id === 'string' && id.trim() ? id.trim() : expectedId
  return { id: favId, kataId, techniqueId, createdAt }
}

function validateElementMark(raw: unknown, path: string): ElementMarkState | string {
  if (!isPlainObject(raw)) return `${path} must be an object.`
  if (typeof raw.elementId !== 'string' || !raw.elementId.trim()) {
    return `${path}.elementId is required.`
  }
  if (typeof raw.reviewed !== 'boolean') return `${path}.reviewed must be a boolean.`
  if (typeof raw.forgotten !== 'boolean') return `${path}.forgotten must be a boolean.`
  if (!Array.isArray(raw.criteria)) return `${path}.criteria must be an array.`
  const criteria: ElementMarkState['criteria'] = []
  const seen = new Set<string>()
  for (let i = 0; i < raw.criteria.length; i++) {
    const row = raw.criteria[i]
    if (!isPlainObject(row)) return `${path}.criteria[${i}] must be an object.`
    if (typeof row.criterionId !== 'string' || !row.criterionId.trim()) {
      return `${path}.criteria[${i}].criterionId is required.`
    }
    if (typeof row.severity !== 'string' || !SEVERITIES.has(row.severity as MistakeSeverity)) {
      return `${path}.criteria[${i}].severity must be small, medium, or big.`
    }
    if (seen.has(row.criterionId)) {
      return `${path}.criteria: duplicate criterionId ${row.criterionId}.`
    }
    seen.add(row.criterionId)
    criteria.push({
      criterionId: row.criterionId.trim(),
      severity: row.severity as MistakeSeverity,
    })
  }
  if (raw.comment !== undefined && typeof raw.comment !== 'string') {
    return `${path}.comment must be a string when present.`
  }
  if (typeof raw.comment === 'string' && raw.comment.length > 4000) {
    return `${path}.comment is too long (max 4000 characters).`
  }
  const comment =
    typeof raw.comment === 'string' && raw.comment.length > 0 ? raw.comment : undefined
  return {
    elementId: raw.elementId.trim(),
    reviewed: raw.reviewed,
    forgotten: raw.forgotten,
    criteria: raw.forgotten ? [] : criteria,
    comment,
  }
}

function validatePerformance(
  raw: unknown,
  index: number,
  catalogIndex: ReturnType<typeof knownCatalog>,
): KataPerformance | string {
  if (!isPlainObject(raw)) return `performances[${index}] must be an object.`
  const path = `performances[${index}]`
  const {
    id,
    kataId,
    status,
    createdAt,
    updatedAt,
    completedAt,
    scoredBy,
    note,
    ruleTableVersion,
    judgeCount,
    elementMarks,
    rawTotal,
    forgottenPenaltyApplied,
    finalTotal,
    maxTotal,
  } = raw

  if (typeof id !== 'string' || !id.trim()) return `${path}.id is required.`
  if (!isKataId(kataId, catalogIndex.kataIds)) return `${path}.kataId is unknown.`
  if (!getKataScoring(kataId)) {
    return `${path}.kataId does not support scoring yet.`
  }
  if (status !== 'draft' && status !== 'completed') {
    return `${path}.status must be draft or completed.`
  }
  if (!isIsoTimestamp(createdAt) || !isIsoTimestamp(updatedAt)) {
    return `${path} timestamps must be ISO strings.`
  }
  if (completedAt !== undefined && !isIsoTimestamp(completedAt)) {
    return `${path}.completedAt must be an ISO timestamp when present.`
  }
  if (scoredBy !== undefined && typeof scoredBy !== 'string') {
    return `${path}.scoredBy must be a string.`
  }
  if (note !== undefined && typeof note !== 'string') {
    return `${path}.note must be a string.`
  }
  if (typeof ruleTableVersion !== 'string' || !ruleTableVersion.trim()) {
    return `${path}.ruleTableVersion is required.`
  }
  if (judgeCount !== 1) return `${path}.judgeCount must be 1.`
  if (!Array.isArray(elementMarks)) return `${path}.elementMarks must be an array.`

  const marks: ElementMarkState[] = []
  const seenElements = new Set<string>()
  for (let i = 0; i < elementMarks.length; i++) {
    const mark = validateElementMark(elementMarks[i], `${path}.elementMarks[${i}]`)
    if (typeof mark === 'string') return mark
    if (seenElements.has(mark.elementId)) {
      return `${path}.elementMarks: duplicate elementId ${mark.elementId}.`
    }
    seenElements.add(mark.elementId)
    marks.push(mark)
  }

  if (typeof rawTotal !== 'number' || !Number.isFinite(rawTotal) || rawTotal < 0) {
    return `${path}.rawTotal must be a finite number ≥ 0.`
  }
  if (typeof finalTotal !== 'number' || !Number.isFinite(finalTotal) || finalTotal < 0) {
    return `${path}.finalTotal must be a finite number ≥ 0.`
  }
  if (typeof maxTotal !== 'number' || !Number.isFinite(maxTotal) || maxTotal < 0) {
    return `${path}.maxTotal must be a finite number ≥ 0.`
  }
  if (typeof forgottenPenaltyApplied !== 'boolean') {
    return `${path}.forgottenPenaltyApplied must be a boolean.`
  }

  return {
    id: id.trim(),
    kataId,
    status,
    createdAt,
    updatedAt,
    completedAt: typeof completedAt === 'string' ? completedAt : undefined,
    scoredBy: typeof scoredBy === 'string' ? scoredBy : undefined,
    note: typeof note === 'string' ? note : undefined,
    ruleTableVersion: ruleTableVersion.trim(),
    judgeCount: 1,
    elementMarks: marks,
    rawTotal,
    forgottenPenaltyApplied,
    finalTotal,
    maxTotal,
  }
}

function validatePreferences(
  raw: unknown,
  catalogIndex: ReturnType<typeof knownCatalog>,
): AppPreferences | string {
  if (raw === undefined || raw === null) return {}
  if (!isPlainObject(raw)) return 'preferences must be an object.'
  const prefs: AppPreferences = {}
  if (raw.lastKataId !== undefined) {
    if (!isKataId(raw.lastKataId, catalogIndex.kataIds)) {
      return 'preferences.lastKataId is unknown.'
    }
    prefs.lastKataId = raw.lastKataId
  }
  if (raw.lastTechniqueId !== undefined) {
    if (typeof raw.lastTechniqueId !== 'string' || !catalogIndex.techniqueIds.has(raw.lastTechniqueId)) {
      return 'preferences.lastTechniqueId is unknown.'
    }
    prefs.lastTechniqueId = raw.lastTechniqueId
  }
  if (prefs.lastKataId && prefs.lastTechniqueId) {
    const allowed = catalogIndex.techniquesByKata.get(prefs.lastKataId)
    if (!allowed?.has(prefs.lastTechniqueId)) {
      return 'preferences.lastTechniqueId does not belong to lastKataId.'
    }
  }
  if (raw.deferYoutubeLoad !== undefined) {
    if (typeof raw.deferYoutubeLoad !== 'boolean') {
      return 'preferences.deferYoutubeLoad must be a boolean.'
    }
    prefs.deferYoutubeLoad = raw.deferYoutubeLoad
  }
  return prefs
}

/**
 * Validate a complete import payload before any writes.
 * Rejects malformed/unsupported data with readable errors.
 */
export function validateImportPayload(raw: unknown): ValidateImportResult {
  if (!isPlainObject(raw)) {
    return { ok: false, error: 'Import must be a JSON object.' }
  }

  const schemaVersion = raw.schemaVersion
  if (
    typeof schemaVersion !== 'number' ||
    !SUPPORTED_IMPORT_SCHEMA_VERSIONS.includes(schemaVersion as 1 | 2 | 3 | 4)
  ) {
    return {
      ok: false,
      error: `Unsupported schemaVersion. Supported: ${SUPPORTED_IMPORT_SCHEMA_VERSIONS.join(', ')}.`,
    }
  }

  const catalogIndex = knownCatalog()

  if (!Array.isArray(raw.references)) {
    return { ok: false, error: 'references must be an array.' }
  }
  if (raw.references.length > MAX_REFERENCES) {
    return { ok: false, error: `Too many references (max ${MAX_REFERENCES}).` }
  }

  const references: VideoReference[] = []
  const refIds = new Set<string>()
  let segmentCount = 0
  for (let i = 0; i < raw.references.length; i++) {
    const ref = validateReference(raw.references[i], i, catalogIndex)
    if (typeof ref === 'string') return { ok: false, error: ref }
    if (refIds.has(ref.id)) return { ok: false, error: `Duplicate reference id ${ref.id}.` }
    refIds.add(ref.id)
    segmentCount += ref.segments.length
    references.push(ref)
  }

  const notesRaw = raw.notes
  if (schemaVersion >= 2) {
    if (notesRaw !== undefined && !Array.isArray(notesRaw)) {
      return { ok: false, error: 'notes must be an array.' }
    }
  } else if (notesRaw !== undefined && !Array.isArray(notesRaw)) {
    return { ok: false, error: 'notes must be an array when present.' }
  }
  const notesList = Array.isArray(notesRaw) ? notesRaw : []
  if (notesList.length > MAX_NOTES) {
    return { ok: false, error: `Too many notes (max ${MAX_NOTES}).` }
  }
  const notes: TechniqueNote[] = []
  const noteIds = new Set<string>()
  for (let i = 0; i < notesList.length; i++) {
    const note = validateNote(notesList[i], i, catalogIndex)
    if (typeof note === 'string') return { ok: false, error: note }
    if (noteIds.has(note.id)) return { ok: false, error: `Duplicate note id ${note.id}.` }
    noteIds.add(note.id)
    notes.push(note)
  }

  const favsRaw = raw.favourites
  if (favsRaw !== undefined && !Array.isArray(favsRaw)) {
    return { ok: false, error: 'favourites must be an array.' }
  }
  const favsList = Array.isArray(favsRaw) ? favsRaw : []
  if (favsList.length > MAX_FAVOURITES) {
    return { ok: false, error: `Too many favourites (max ${MAX_FAVOURITES}).` }
  }
  const favourites: Favourite[] = []
  const favIds = new Set<string>()
  for (let i = 0; i < favsList.length; i++) {
    const fav = validateFavourite(favsList[i], i, catalogIndex)
    if (typeof fav === 'string') return { ok: false, error: fav }
    if (favIds.has(fav.id)) return { ok: false, error: `Duplicate favourite id ${fav.id}.` }
    favIds.add(fav.id)
    favourites.push(fav)
  }

  const preferences = validatePreferences(raw.preferences, catalogIndex)
  if (typeof preferences === 'string') return { ok: false, error: preferences }

  const performancesRaw = raw.performances
  if (performancesRaw !== undefined && !Array.isArray(performancesRaw)) {
    return { ok: false, error: 'performances must be an array.' }
  }
  const performancesList = Array.isArray(performancesRaw) ? performancesRaw : []
  if (performancesList.length > MAX_PERFORMANCES) {
    return { ok: false, error: `Too many performances (max ${MAX_PERFORMANCES}).` }
  }
  const performances: KataPerformance[] = []
  const performanceIds = new Set<string>()
  for (let i = 0; i < performancesList.length; i++) {
    const perf = validatePerformance(performancesList[i], i, catalogIndex)
    if (typeof perf === 'string') return { ok: false, error: perf }
    if (performanceIds.has(perf.id)) {
      return { ok: false, error: `Duplicate performance id ${perf.id}.` }
    }
    performanceIds.add(perf.id)
    performances.push(perf)
  }

  const exportedAt =
    raw.exportedAt === undefined
      ? undefined
      : isIsoTimestamp(raw.exportedAt)
        ? raw.exportedAt
        : null
  if (exportedAt === null) {
    return { ok: false, error: 'exportedAt must be an ISO timestamp when present.' }
  }

  const payload: ExportPayload = {
    schemaVersion: SCHEMA_VERSION,
    exportedAt: exportedAt ?? new Date().toISOString(),
    references,
    notes,
    favourites,
    preferences,
    performances,
  }

  return {
    ok: true,
    payload,
    preview: {
      schemaVersion,
      exportedAt,
      referenceCount: references.length,
      segmentCount,
      noteCount: notes.length,
      favouriteCount: favourites.length,
      performanceCount: performances.length,
      hasPreferences: Boolean(
        preferences.lastKataId ||
          preferences.lastTechniqueId ||
          preferences.deferYoutubeLoad !== undefined,
      ),
    },
  }
}

/** Parse + validate JSON text with size limit. Existing data untouched on failure. */
export function parseAndValidateImportJson(text: string): ValidateImportResult {
  if (new TextEncoder().encode(text).length > MAX_IMPORT_BYTES) {
    return {
      ok: false,
      error: `Import file is too large (max ${Math.round(MAX_IMPORT_BYTES / 1024 / 1024)} MB).`,
    }
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(text) as unknown
  } catch {
    return { ok: false, error: 'Import file is not valid JSON.' }
  }
  return validateImportPayload(parsed)
}
